import { supabase } from '../lib/supabase'
import { supersedeAndAssign } from '../lib/jobAssignmentHistory'
import { gateJobSiteAction } from '../lib/fieldGpsGate'
import { resolveEmployeeIdForUser } from '../lib/timeClockActions'

export type JobTaskSource = 'manual' | 'visit_group' | 'template' | 'work_spec'

export interface JobTask {
  id: string
  job_id: string
  label: string
  is_done: boolean
  sort_order: number
  source?: JobTaskSource
  completed_at?: string | null
  photo_required?: boolean
  job_line_item_unit_id?: string | null
}

export interface JobComment {
  id: string
  job_id: string
  author_id?: string | null
  body: string
  created_at: string
}

export type JobCaptureSource = 'upload' | 'camera'

export interface JobAttachment {
  id: string
  job_id: string
  file_url: string
  file_name?: string | null
  mime_type?: string | null
  attachment_type: string
  caption?: string | null
  capture_source?: JobCaptureSource
  job_task_id?: string | null
  latitude?: number | null
  longitude?: number | null
  accuracy_m?: number | null
  captured_at?: string | null
  employee_id?: string | null
  metadata?: Record<string, unknown> | null
  created_at: string
}

export async function fetchJobTasks(jobId: string): Promise<JobTask[]> {
  const { data, error } = await supabase
    .from('job_tasks')
    .select('*')
    .eq('job_id', jobId)
    .order('sort_order', { ascending: true })
  if (error) throw error
  return data || []
}

export async function addJobTask(
  jobId: string,
  label: string,
  sortOrder: number,
  source: JobTaskSource = 'manual',
  photoRequired = false,
  jobLineItemUnitId?: string | null
): Promise<void> {
  const { error } = await supabase.from('job_tasks').insert({
    job_id: jobId,
    job_line_item_unit_id: jobLineItemUnitId || null,
    label,
    sort_order: sortOrder,
    source,
    photo_required: photoRequired,
  })
  if (error) throw error
}

export async function addJobTasksBulk(
  jobId: string,
  items: Array<{ label: string; sort_order?: number; photo_required?: boolean }>,
  source: JobTaskSource = 'template',
  jobLineItemUnitId?: string | null
): Promise<void> {
  if (!items.length) return
  const { data: existing } = await supabase
    .from('job_tasks')
    .select('sort_order')
    .eq('job_id', jobId)
    .order('sort_order', { ascending: false })
    .limit(1)
  const start = existing?.[0]?.sort_order != null ? Number(existing[0].sort_order) + 1 : 0
  const rows = items
    .map((item, index) => ({
      job_id: jobId,
      job_line_item_unit_id: jobLineItemUnitId || null,
      label: item.label.trim(),
      sort_order: start + (item.sort_order ?? index),
      source,
      photo_required: !!item.photo_required,
    }))
    .filter((r) => r.label.length > 0)
  if (rows.length === 0) return
  const { error } = await supabase.from('job_tasks').insert(rows)
  if (error) throw error
}

export async function setJobTaskPhotoRequired(
  taskId: string,
  photoRequired: boolean
): Promise<void> {
  const { error } = await supabase
    .from('job_tasks')
    .update({
      photo_required: photoRequired,
      updated_at: new Date().toISOString(),
    })
    .eq('id', taskId)
  if (error) throw error
}

export async function countJobPhotos(
  jobId: string,
  cameraOnly = false
): Promise<number> {
  let query = supabase
    .from('job_attachments')
    .select('id', { count: 'exact', head: true })
    .eq('job_id', jobId)
    .eq('attachment_type', 'photo')
  if (cameraOnly) {
    query = query.eq('capture_source', 'camera')
  }
  const { count, error } = await query
  if (error) throw error
  return count || 0
}

export async function toggleJobTask(taskId: string, isDone: boolean): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  const { error } = await supabase
    .from('job_tasks')
    .update({
      is_done: isDone,
      completed_by: isDone ? user?.id || null : null,
      completed_at: isDone ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', taskId)
  if (error) throw error
}

/**
 * Persist is_done for photo_required tasks that already have a linked photo
 * but were left unchecked (e.g. auto-check raced the photo-required trigger).
 * Returns the number of tasks marked done.
 */
export async function reconcilePhotoRequiredTasksDone(jobId: string): Promise<number> {
  const { data: tasks, error } = await supabase
    .from('job_tasks')
    .select('id')
    .eq('job_id', jobId)
    .eq('photo_required', true)
    .eq('is_done', false)
  if (error) throw error
  if (!tasks?.length) return 0

  const { data: photos, error: photoError } = await supabase
    .from('job_attachments')
    .select('job_task_id')
    .eq('job_id', jobId)
    .eq('attachment_type', 'photo')
    .in(
      'job_task_id',
      tasks.map((t) => t.id)
    )
  if (photoError) throw photoError

  const covered = new Set(
    (photos || []).map((p) => p.job_task_id).filter((id): id is string => !!id)
  )
  const toMark = tasks.filter((t) => covered.has(t.id))
  for (const task of toMark) {
    await toggleJobTask(task.id, true)
  }
  return toMark.length
}

export async function deleteJobTask(taskId: string): Promise<void> {
  const { error } = await supabase.from('job_tasks').delete().eq('id', taskId)
  if (error) throw error
}

export async function fetchJobComments(jobId: string): Promise<JobComment[]> {
  const { data, error } = await supabase
    .from('job_comments')
    .select('*')
    .eq('job_id', jobId)
    .order('created_at', { ascending: true })
  if (error) throw error
  return data || []
}

export async function addJobComment(jobId: string, body: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()
  const { error } = await supabase.from('job_comments').insert({
    job_id: jobId,
    author_id: user?.id || null,
    body,
  })
  if (error) throw error
}

export type JobAttachmentWithDetails = JobAttachment & {
  employee?: { first_name?: string | null; last_name?: string | null } | null
  job_task?: { label?: string | null } | null
}

export async function fetchJobAttachments(jobId: string): Promise<JobAttachmentWithDetails[]> {
  const { data, error } = await supabase
    .from('job_attachments')
    .select(
      `
      *,
      employee:employees(first_name, last_name),
      job_task:job_tasks(label)
    `
    )
    .eq('job_id', jobId)
    .order('created_at', { ascending: false })
  if (error) {
    // Fallback if joins fail on older schemas
    const { data: plain, error: plainError } = await supabase
      .from('job_attachments')
      .select('*')
      .eq('job_id', jobId)
      .order('created_at', { ascending: false })
    if (plainError) throw plainError
    return plain || []
  }
  return data || []
}

export async function uploadJobFile(
  jobId: string,
  file: File,
  attachmentType: 'photo' | 'document' = 'photo',
  caption?: string,
  captureSource: JobCaptureSource = 'upload',
  jobTaskId?: string | null
): Promise<JobAttachment> {
  // GPS + geofence before storage upload so failed gates leave no orphan files.
  const gated = await gateJobSiteAction({
    jobId,
    actionLabel: jobTaskId ? 'Checklist photo' : 'Job photo',
    attemptType: jobTaskId ? 'checklist_photo' : 'job_photo',
  })
  const position = gated.geo.position!

  const ext = file.name.split('.').pop() || 'bin'
  const storagePath = `${jobId}/${Date.now()}.${ext}`
  const { error: uploadError } = await supabase.storage.from('job-files').upload(storagePath, file)
  if (uploadError) throw uploadError

  const { data: urlData } = supabase.storage.from('job-files').getPublicUrl(storagePath)
  const {
    data: { user },
  } = await supabase.auth.getUser()

  const employeeId = user?.id ? await resolveEmployeeIdForUser(user.id) : null
  const capturedAt = position.capturedAt || new Date().toISOString()
  const metadata: Record<string, unknown> = {
    job_id: jobId,
    job_task_id: jobTaskId || null,
    employee_id: employeeId,
    user_id: user?.id || null,
    capture_source: captureSource,
    captured_at: capturedAt,
    file_name: file.name,
    mime_type: file.type || null,
    accuracy_m: position.accuracy,
    distance_ft: gated.distanceFt,
    geofence_overridden: gated.geofenceOverridden,
  }

  const { data: inserted, error } = await supabase
    .from('job_attachments')
    .insert({
      job_id: jobId,
      file_url: urlData.publicUrl,
      file_name: file.name,
      mime_type: file.type || null,
      attachment_type: attachmentType,
      caption: caption || null,
      capture_source: captureSource,
      job_task_id: jobTaskId || null,
      uploaded_by: user?.id || null,
      latitude: position.latitude,
      longitude: position.longitude,
      accuracy_m: position.accuracy,
      captured_at: capturedAt,
      employee_id: employeeId,
      metadata,
    })
    .select('*')
    .single()

  if (error) throw error

  // Mirror to legacy job_photos table when available (best-effort)
  const { error: photoError } = await supabase.from('job_photos').insert({
    job_id: jobId,
    file_url: urlData.publicUrl,
    file_name: file.name,
    photo_type: attachmentType === 'photo' ? 'completion' : 'issue',
    caption: caption || file.name,
  })
  if (photoError) {
    // Legacy table may be absent or schema may differ
  }

  return (
    inserted || {
      id: `uploaded-${Date.now()}`,
      job_id: jobId,
      job_task_id: jobTaskId || null,
      file_url: urlData.publicUrl,
      file_name: file.name,
      mime_type: file.type || null,
      attachment_type: attachmentType,
      caption: caption || null,
      capture_source: captureSource,
      created_at: capturedAt,
    }
  )
}

export async function deleteJobAttachment(attachmentId: string): Promise<void> {
  const { error } = await supabase.from('job_attachments').delete().eq('id', attachmentId)
  if (error) throw error
}

/** Tasks that require a photo but have none linked. */
export async function fetchTasksMissingRequiredPhotos(
  jobId: string
): Promise<Array<{ id: string; label: string }>> {
  const { data: tasks, error } = await supabase
    .from('job_tasks')
    .select('id, label')
    .eq('job_id', jobId)
    .eq('photo_required', true)
  if (error) throw error
  if (!tasks?.length) return []

  const { data: photos, error: photoError } = await supabase
    .from('job_attachments')
    .select('job_task_id')
    .eq('job_id', jobId)
    .eq('attachment_type', 'photo')
    .in(
      'job_task_id',
      tasks.map((t) => t.id)
    )
  if (photoError) throw photoError

  const covered = new Set(
    (photos || []).map((p) => p.job_task_id).filter((id): id is string => !!id)
  )
  return tasks.filter((t) => !covered.has(t.id))
}

export async function assignJobCrew(jobId: string, crewId: string | null): Promise<void> {
  await supersedeAndAssign({ jobId, crewId })
}

export async function assignJobEmployee(jobId: string, employeeId: string | null): Promise<void> {
  await supersedeAndAssign({ jobId, employeeId })
}

export type CrewOption = { id: string; name: string; is_subcontractor?: boolean | null }
export type EmployeeOption = {
  id: string
  first_name: string
  last_name: string
  employee_number?: string | null
}

const EMPLOYEE_OPTION_SELECT = 'id, first_name, last_name, employee_number'

export async function fetchCrewsAndEmployees(): Promise<{
  crews: CrewOption[]
  employees: EmployeeOption[]
  loadError?: string
}> {
  const active = await Promise.all([
    supabase
      .from('crews')
      .select('id, name, is_subcontractor')
      .eq('is_active', true)
      .order('name'),
    supabase
      .from('employees')
      .select(EMPLOYEE_OPTION_SELECT)
      .eq('is_active', true)
      .order('first_name'),
  ])

  let crewsRes = active[0]
  let empRes = active[1]

  // Missing column / RLS / other filter failure → retry without is_active.
  if (crewsRes.error || empRes.error) {
    const unfiltered = await Promise.all([
      supabase.from('crews').select('id, name, is_subcontractor').order('name'),
      supabase.from('employees').select(EMPLOYEE_OPTION_SELECT).order('first_name'),
    ])
    crewsRes = unfiltered[0]
    empRes = unfiltered[1]
  }

  if (crewsRes.error || empRes.error) {
    const msg = crewsRes.error?.message || empRes.error?.message || 'Failed to load crews/employees'
    return { crews: [], employees: [], loadError: msg }
  }

  let crews = (crewsRes.data || []) as CrewOption[]
  let employees = (empRes.data || []) as EmployeeOption[]

  // Active filter returned nothing — include inactive/null so assignment is still possible.
  if (crews.length === 0 && employees.length === 0) {
    const unfiltered = await Promise.all([
      supabase.from('crews').select('id, name, is_subcontractor').order('name'),
      supabase.from('employees').select(EMPLOYEE_OPTION_SELECT).order('first_name'),
    ])
    if (!unfiltered[0].error && !unfiltered[1].error) {
      crews = (unfiltered[0].data || []) as CrewOption[]
      employees = (unfiltered[1].data || []) as EmployeeOption[]
    }
  }

  return { crews, employees }
}
